import { darken } from '@adornis/chemistry/elements/theming/color-shades.js';
import { Color, ColorScheme, type Tones } from '@adornis/chemistry/elements/theming/coloring-system.js';
import { DesignSystem, breakPoints, type ThemeDefinition } from '@adornis/chemistry/elements/theming/design.js';
import '@adornis/fonts/fontSrc/lato.js';
import { config } from '@ils/config/config.js';
import { ExamDayRatingType, ExamType } from '@ils/static/enums.js';

export const colors = {
  accent: (config.get('ACCENTCOLOR') || '#59b7ff') as `#${string}`,
  blue: '#648DBF',
  orange: 'orange',
  green: '#68DC73',
  red: '#A40606',
  bayernBlue: '#0090cf',
  rlpYellow: '#fcdd08',
  famedAccent: '#59b7ff',
  famedPrimary: '#648dbf',
  famedSecondary: '#7b8c92',
  fsppAccent: '#7ed9c6',
  fsppPrimary: '#7ed9c6',
  fsppSecondary: '#004856',
} as const;

const roleColors = {
  doctor: colors.blue,
  patient: colors.red,
  nurse: colors.green,
};

export class ILSColorScheme extends ColorScheme<Tones> {
  blue = Color.make(colors.blue);
  orange = Color.make(colors.orange);
  green = Color.make(colors.green);
  red = Color.make(colors.red);

  role = {
    doctor: Color.make(roleColors.doctor),
    patient: Color.make(roleColors.patient),
    nurse: Color.make(roleColors.nurse),
  };
  famedPrimary = Color.make(colors.famedPrimary);
  famedSecondary = Color.make(colors.famedSecondary);
  fsppPrimary = Color.make(colors.fsppPrimary);
  fsppSecondary = Color.make(colors.fsppSecondary);
  bayernBlue = Color.make(colors.bayernBlue);
  rlpYellow = Color.make(colors.rlpYellow);
}

export interface ILSThemeDefinition extends ThemeDefinition {
  colors: ILSColorScheme;
}

export function getColorForRatingType(examDayType: ExamDayRatingType): [string, string] {
  const currentTheme = DesignSystem.currentTheme.getValue() as ILSThemeDefinition;
  switch (examDayType) {
    case ExamDayRatingType.BAYERN:
      return [currentTheme.colors.bayernBlue, currentTheme.colors.on.bayernBlue];
    case ExamDayRatingType.RLP:
      return [currentTheme.colors.rlpYellow, currentTheme.colors.on.rlpYellow];
    default:
      return [currentTheme.colors.accent, currentTheme.colors.on.accent];
  }
}
export function getColorForExamType(examType: ExamType): [string, string] {
  const currentTheme = DesignSystem.currentTheme.getValue() as ILSThemeDefinition;
  switch (examType) {
    case ExamType.FAMED:
      return [currentTheme.colors.famedPrimary, currentTheme.colors.on.famedPrimary];
    case ExamType.FSPP:
      return [currentTheme.colors.fsppPrimary, currentTheme.colors.on.fsppPrimary];
    default:
      return [currentTheme.colors.accent, currentTheme.colors.on.accent];
  }
}

export const getToneForExamType = (examType: ExamType) =>
  ({
    [ExamType.FAMED]: 'famedPrimary',
    [ExamType.FSPP]: 'fsppPrimary',
  }[examType] ?? 'accent');

const chemistry = DesignSystem.getTheme('default')!;

const baseColorTheme = {
  ...colors,
  neutral: chemistry.colors.neutral.string,
  primary: chemistry.colors.neutral.shade(200).string,
  secondary: chemistry.colors.secondary.string,

  tone: {
    placeholder: chemistry.colors.neutral.string,
    active: darken(colors.accent, 2),
    error: colors.red,
    success: colors.green,
    warning: colors.orange,
    subtle: chemistry.colors.neutral.shade(300),
    famedPrimary: colors.famedPrimary,
    famedSecondary: colors.famedSecondary,
    fsppPrimary: colors.fsppPrimary,
    fsppSecondary: colors.fsppSecondary,
    bayernBlue: colors.bayernBlue,
    rlpYellow: colors.rlpYellow,
  },
};

const baseThemeDefinition = {
  colors: new ILSColorScheme(baseColorTheme),
  sizes: {
    breakPoints,
    borderRadius: '5px',
    borderRadiusSecondary: '3px',
    maxPageWidth: chemistry.sizes.maxPageWidth,
    maxReadableWidth: chemistry.sizes.maxReadableWidth,
  },
  baseFontSize: chemistry.baseFontSize,
  baseFontSizePrint: chemistry.baseFontSize,
  borders: chemistry.borders,
  defaults: chemistry.defaults,
  fontFamily: 'Lato',
  fontFamilyHeading: 'Lato',
  spacing: chemistry.spacing,
};

export const famedThemeDefinition = {
  ...baseThemeDefinition,
  colors: new ILSColorScheme({
    ...baseColorTheme,
    tone: {
      ...baseColorTheme.tone,
      active: darken(colors.famedAccent, 2),
    },
    accent: colors.famedAccent,
    primary: colors.famedPrimary,
    secondary: colors.famedSecondary,
  }),
};
export const fsppThemeDefinition = {
  ...baseThemeDefinition,
  colors: new ILSColorScheme({
    ...baseColorTheme,
    tone: {
      ...baseColorTheme.tone,
      active: darken(colors.fsppAccent, 2),
    },
    accent: colors.fsppAccent,
    primary: colors.fsppPrimary,
    secondary: colors.fsppSecondary,
  }),
};
